/**
 * Helper methods for pause order logic
 */

/**
 * Checks if an outlet is currently paused based on closed_till date
 * @param {string} closedTill - Date string in format "YYYY-MM-DD HH:MM"
 * @returns {boolean} - true if outlet is paused, false if operational
 */
function isOutletPaused(closedTill: string): boolean {
  if (!closedTill) return false;

  const closedTillDate = new Date(closedTill);
  const currentDate = new Date();

  return currentDate < closedTillDate;
}

/**
 * Gets all delivery outlets from businessOnOff object
 * @param {Object} businessOnOff - The business on/off configuration object
 * @returns {Array} - Array of delivery outlet objects
 */
function getDeliveryOutlets(businessOnOff: object): Array<any> {
  return Object.entries(businessOnOff)
    .filter(([key, value]) => value.type === "delivery")
    .map(([key, value]) => ({ key, ...value }));
}

/**
 * Finds outlet by outlet_shared_id
 * @param {Object} businessOnOff - The business on/off configuration object
 * @param {string} outletSharedId - The outlet_shared_id to search for
 * @param {string} [orderType] - Optional order type filter ('delivery' or 'pickup')
 * @returns {Object|null} - Outlet object or null if not found
 */
function findOutletBySharedId(
  businessOnOff: object,
  outletSharedId: string,
  orderType: string = null
): object | null {
  const entries = Object.entries(businessOnOff);

  for (const [key, outlet] of entries) {
    if (outlet.outlet_shared_id === outletSharedId) {
      // If orderType is specified, check if it matches
      if (orderType && outlet.type !== orderType) {
        continue;
      }
      return { key, ...outlet };
    }
  }

  return null;
}

/**
 * Checks if orders are paused based on outlet configuration
 * @param {Object} businessOnOff - The business on/off configuration object
 * @param {string|number} [identifier] - Optional outlet ID or outlet_shared_id
 * @param {string} [orderType='delivery'] - Order type: 'delivery' or 'pickup'
 * @returns {boolean} - true if orders are paused, false if operational
 */
function isOrderPaused(
  businessOnOff: object,
  identifier: string | number = null,
  orderType: string = "delivery"
): boolean {
  if (!businessOnOff || Object.keys(businessOnOff).length === 0) {
    return false;
  }

  const deliveryOutlets = getDeliveryOutlets(businessOnOff);
  let outlet = null;

  // If identifier is provided, try to find the outlet
  if (identifier !== null) {
    // First, try to find by outlet_shared_id (UUID format)
    if (typeof identifier === "string" && identifier.includes("-")) {
      outlet = findOutletBySharedId(businessOnOff, identifier, orderType);
    }

    // If not found, try with outlet_id
    if (!outlet) {
      const key = `${identifier}_${orderType}`;
      outlet = businessOnOff[key];
    }

    if (!outlet) return false;

    return isOutletPaused(outlet.closed_till);
  }

  // If no identifier provided, check based on number of delivery outlets
  if (deliveryOutlets.length === 1) {
    // Single delivery outlet - check its status
    return isOutletPaused(deliveryOutlets[0].closed_till);
  } else if (deliveryOutlets.length > 1) {
    // Multiple outlets but no identifier provided - cannot determine status
    console.warn("Multiple outlets found but no identifier provided");
    return false;
  }

  return false;
}

/**
 * Gets the pause status and details for an outlet
 * @param {Object} businessOnOff - The business on/off configuration object
 * @param {string|number} [identifier] - Optional outlet ID or outlet_shared_id
 * @param {string} [orderType='delivery'] - Order type: 'delivery' or 'pickup'
 * @returns {Object} - Object with isPaused boolean and outlet details
 */
function getOrderPauseStatus(
  businessOnOff: object,
  identifier: string | number = null,
  orderType: string = "delivery"
): object {
  if (!businessOnOff || Object.keys(businessOnOff).length === 0) {
    return { isPaused: false, outlet: null };
  }

  const deliveryOutlets = getDeliveryOutlets(businessOnOff);
  let outlet = null;

  if (identifier !== null) {
    // First, try to find by outlet_shared_id (UUID format)
    if (typeof identifier === "string" && identifier.includes("-")) {
      outlet = findOutletBySharedId(businessOnOff, identifier, orderType);
    }

    // If not found, try with outlet_id
    if (!outlet) {
      const key = `${identifier}_${orderType}`;
      outlet = businessOnOff[key];
    }
  } else if (deliveryOutlets.length === 1) {
    outlet = deliveryOutlets[0];
  }

  if (!outlet) {
    return { isPaused: false, outlet: null };
  }

  const isPaused = isOutletPaused(outlet.closed_till);

  return {
    isPaused,
    outlet: {
      outletId: outlet.outet_id || outlet.outlet_id,
      outletSharedId: outlet.outlet_shared_id,
      outletName: outlet.outlet_name,
      closedTill: outlet.closed_till,
      type: outlet.type,
      resumesAt: isPaused
        ? new Date(outlet.closed_till).toLocaleString()
        : null,
    },
  };
}

/**
 * Gets all outlets (delivery and pickup) for a specific outlet_shared_id
 * @param {Object} businessOnOff - The business on/off configuration object
 * @param {string} outletSharedId - The outlet_shared_id to search for
 * @returns {Array} - Array of outlet objects with same shared ID
 */
function getOutletsBySharedId(
  businessOnOff: object,
  outletSharedId: string
): Array<any> {
  return Object.entries(businessOnOff)
    .filter(([key, outlet]) => outlet.outlet_shared_id === outletSharedId)
    .map(([key, outlet]) => ({ key, ...outlet }));
}

// Example usage:
// const businessOnOff = window.businessOnOff;

// Check using outlet_shared_id
// const isPaused1 = isOrderPaused(businessOnOff, '127e7953-4513-4d8d-ba69-b78fbfb8922f', 'pickup');
// const isPaused2 = isOrderPaused(businessOnOff, '5a4ed45e-626c-4103-860f-9dd23283e600', 'delivery');

// Check using outlet_id
// const isPaused3 = isOrderPaused(businessOnOff, 4, 'pickup');
// const isPaused4 = isOrderPaused(businessOnOff, 134, 'delivery');

// Auto-detect single delivery outlet
// const isPausedAuto = isOrderPaused(businessOnOff);

// Get detailed status using shared_id
// const status = getOrderPauseStatus(businessOnOff, '5a4ed45e-626c-4103-860f-9dd23283e600', 'delivery');
// console.log(status.isPaused, status.outlet);

// Get all outlets for a shared_id
// const outlets = getOutletsBySharedId(businessOnOff, '127e7953-4513-4d8d-ba69-b78fbfb8922f');

// Export for use in modules (if using ES6 modules)
export {
  isOrderPaused,
  getOrderPauseStatus,
  isOutletPaused,
  getDeliveryOutlets,
  findOutletBySharedId,
  getOutletsBySharedId,
};
